import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Dropdown, Box, Flex, Text, DatePicker } from "samba-ui";
import moment from "moment";
import styled from "styled-components";
import KantarFilterExpression from "./KantarFilterExpression";
import { DateContainer } from "../../../Common/CreateFeedModal/DatesAndCriteria/styles";
import { updateFeed } from "../../../../actions/feed";
import { getFeedMinAndMaxDates, getMinDateWithReportConfig } from "../../../../utils";
import {
  minStarDateForGeoWeights,
  minStartDateForXref,
  minStartDateForTivoXref,
} from "../../../../constants";
import AreYouSureModal from "../../../Common/AreYouSureModal";
import reportConfigs from "../../../../reducers/reportConfigs";

const DisabledBox = styled(Box)`
  pointer-events: none;
`;

const StyledBox = styled(Box)`
  &:last-child {
    border-bottom: none;
  }
`;

const InfoItem = ({ title, children }) => {
  return (
    <Flex
      justifyContent="space-between"
      borderBottom="1px solid #ececec"
      py={3}
    >
      <Box width={1 / 4}>
        <Text fontSize={1} fontWeight={400}>
          {title}
        </Text>
      </Box>
      <Box
        textAlign={title !== "Kantar Filters" ? "right" : "left"}
        width={3 / 4}
      >
        {children}
      </Box>
    </Flex>
  );
};

InfoItem.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const FeedInfoDetails = ({ feed, updateFeed, reportConfigs }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [typeAndDate, setTypeAndDate] = useState({});

  const {
    user,
    country,
    data_format,
    service_start,
    pbse_version,
    service_stop,
    file_columns = [],
    created_at,
    updated_at,
  } = feed;

  const startDateMaxDate = service_stop
    ? moment(moment.utc(service_stop).format("LLL"))
    : null;
  const startDateSelected = moment(moment.utc(service_start).format("LLL"));
  const endDateSelected = service_stop
    ? moment(moment.utc(service_stop).format("LLL"))
    : null;

  const selectedFileColumns = file_columns.map((f) => ({
    label: f,
    value: f,
    removeIcon: true,
  }));

  const onChooseDate = (type, date) => {
    setIsOpen(true);
    setTypeAndDate({ type, date: moment(date).tz(moment.tz.guess()) });
  };

  const updateFeedDate = () => {
    setIsOpen(false);
    const { type, date } = typeAndDate;
    updateFeed(feed.id, { [type]: date });
  };

  let { minStartDate, minEndDate } = getFeedMinAndMaxDates(feed);
  let maxEndDate = null;
  if (country === "US" && pbse_version === "1.4.2") {
    maxEndDate = moment("2022-06-30");
  }

  if (feed.type === "geo weights") {
    minStartDate = moment(minStarDateForGeoWeights);
  }

  if (feed.type === "x_ref") {
    minStartDate = moment(minStartDateForXref);
  }
  if (feed.type === "tivo x_ref" || feed.type === "tivo+acr x_ref") {
    minStartDate = moment(minStartDateForTivoXref);
  }

  const configTypes = new Set()

  reportConfigs.forEach((config) => {
    configTypes.add(config.type)
  })

  let dateError = null

  if (configTypes.has(feed.type)) {
    const { error: configError, minDate: configMinDate } = getMinDateWithReportConfig(feed, reportConfigs)
    if (configError) {
      dateError = (
      <div
        style={{
          background: '#f44336',
          padding: '10px'
        }}>
          {configError}
        </div>
      )
    } else {
      minStartDate = moment(configMinDate)
    }
  }

  const detailInfo = {
    "Created By": (
      <Text fontSize={1} fontWeight={400}>
        {user}
      </Text>
    ),
    "Created At": (
      <Text fontSize={1} fontWeight={400}>
        {moment(created_at).format("lll")}
      </Text>
    ),
    "Updated At": (
      <Text fontSize={1} fontWeight={400}>
        {updated_at ? moment(updated_at).format("lll") : null}
      </Text>
    ),
    Country: (
      <Flex alignItems="center" justifyContent="flex-end">
        <Box mr={3}>
          <i className={`flag-icon flag-icon-${country.toLowerCase()}`} />
        </Box>
        <Text fontSize={1} fontWeight={400}>
          {country}
        </Text>
      </Flex>
    ),
    "Data production format": (
      <Text fontSize={1} fontWeight={400}>
        {data_format.toUpperCase()}
      </Text>
    ),
    "Start Date": (
      <Box width={300} style={{ float: "right" }}>
      
        <DateContainer>
          <DatePicker
            minDate={minStartDate}
            maxDate={startDateMaxDate}
            selectedDate={startDateSelected}
            onChooseDate={(date) => onChooseDate("service_start", date)}
          />
        </DateContainer>
      </Box>
    ),
    "End Date": (
      <Box width={300} style={{ float: "right" }}>
        <DateContainer>
          <DatePicker
            minDate={minEndDate}
            maxDate={maxEndDate}
            selectedDate={endDateSelected}
            onChooseDate={(date) => onChooseDate("service_stop", date)}
          />
        </DateContainer>
      </Box>
    ),
  };

  if (
    feed.type &&
    feed.type.indexOf("viewership") < 0 &&
    feed.type !== "geo weights"
  ) {
    detailInfo["Kantar Filters"] = (
      <KantarFilterExpression style={{ textAlign: "left" }} feed={feed} />
    );
  }

  return (
    <StyledBox
      border="1"
      borderColor="rgba(155,155,194,0.3)"
      backgroundColor="white"
      width={700}
      padding={40}
      borderRadius={2}
    >
      {dateError}
      {Object.keys(detailInfo).map((title) => (
        <InfoItem key={title} title={title}>
          {detailInfo[title]}
        </InfoItem>
      ))}
      {file_columns.length ? (
        <Box>
          <Box py={3}>
            <Text fontSize={1} fontWeight={400}>
              File Columns
            </Text>
          </Box>
          <DisabledBox>
            <Dropdown isMulti selected={selectedFileColumns} />
          </DisabledBox>
        </Box>
      ) : null}
      <AreYouSureModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={`Update Service ${
          typeAndDate.type === "service_start" ? "Start Date" : "End Date"
        }`}
        body={`Are you sure that you want to update service ${
          typeAndDate.type === "service_start"
            ? `start date to ${moment(typeAndDate.date)
                .tz(moment.tz.guess())
                .format("YYYY-MM-DD")}`
            : `end date to ${moment(typeAndDate.date)
                .tz(moment.tz.guess())
                .format("YYYY-MM-DD")}`
        }`}
        onSubmit={updateFeedDate}
      />
    </StyledBox>
  );
};

FeedInfoDetails.propTypes = {
  feed: PropTypes.shape({
    kantar_filter_expression: PropTypes.object,
    service_start: PropTypes.string,
    service_stop: PropTypes.string,
    country: PropTypes.string,
    user: PropTypes.string,
    data_format: PropTypes.string,
    file_columns: PropTypes.array,
  }),
  updateFeed: PropTypes.func.isRequired,
  reportConfigs: PropTypes.array
};
const mapStateToProps = (store) => ({
  reportConfigs: store.entities.reportConfigs.reportConfigs || [],
});
const mapDispatchToProps = (dispatch) => ({
  updateFeed: (id, body) => dispatch(updateFeed(id, body)),
});
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(FeedInfoDetails);